import React, { useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Phone, Sun, Moon, Menu, X, Globe } from "lucide-react";
import Logo from "./Logo";
import { Button } from "./ui/button";
import { useTheme } from "../lib/theme";
import { useLang } from "../lib/i18n";
import { COMPANY } from "../lib/constants";
import { cn } from "../lib/utils";

export default function Header() {
  const { theme, toggle } = useTheme();
  const { lang, toggle: toggleLang, t } = useLang();
  const [open, setOpen] = useState(false);

  const NAV = [
    { to: "/", label: t.nav.home, end: true, testid: "nav-home" },
    { to: "/services", label: t.nav.services, testid: "nav-services" },
    { to: "/about", label: t.nav.about, testid: "nav-about" },
    { to: "/contact", label: t.nav.contact, testid: "nav-contact" },
  ];

  return (
    <header
      className="sticky top-0 z-50 w-full border-b border-border bg-background/80 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="container-x flex items-center justify-between h-16 sm:h-20">
        <Logo />

        <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "nav-link text-sm font-medium text-muted-foreground hover:text-foreground transition-colors",
                  isActive && "text-foreground active"
                )
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {/* Language toggle */}
          <button
            type="button"
            onClick={toggleLang}
            aria-label={t.common.switchLanguage}
            data-testid="lang-toggle"
            className="h-10 px-3 inline-flex items-center gap-1.5 rounded-full border border-border hover:bg-accent transition-colors text-foreground text-xs font-bold tracking-wider"
          >
            <Globe size={14} aria-hidden="true" />
            <span data-testid="lang-toggle-label">{lang === "en" ? "ES" : "EN"}</span>
          </button>

          {/* Theme toggle */}
          <button
            type="button"
            onClick={toggle}
            aria-label={theme === "light" ? t.common.switchToDark : t.common.switchToLight}
            data-testid="theme-toggle"
            className="h-10 w-10 grid place-items-center rounded-full border border-border hover:bg-accent transition-colors text-foreground"
          >
            {theme === "light" ? <Moon size={18} /> : <Sun size={18} />}
          </button>

          <a
            href={COMPANY.phoneHref}
            className="hidden sm:inline-flex"
            data-testid="header-call-now-link"
          >
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground gap-2 font-semibold rounded-full px-5"
              data-testid="header-call-now-button"
            >
              <Phone size={16} />
              {t.common.callNow}
            </Button>
          </a>

          <a href={COMPANY.phoneHref} className="sm:hidden" data-testid="header-call-now-mobile">
            <Button size="icon" className="bg-primary hover:bg-primary/90 text-primary-foreground rounded-full">
              <Phone size={16} />
            </Button>
          </a>

          <button
            type="button"
            className="md:hidden h-10 w-10 grid place-items-center rounded-full border border-border text-foreground"
            onClick={() => setOpen(true)}
            aria-label={t.common.openMenu}
            data-testid="mobile-menu-open"
          >
            <Menu size={20} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden fixed inset-0 z-[60] bg-background" data-testid="mobile-menu">
          <div className="container-x flex items-center justify-between h-16">
            <Logo size="sm" />
            <button
              type="button"
              className="h-10 w-10 grid place-items-center rounded-full border border-border text-foreground"
              onClick={() => setOpen(false)}
              aria-label={t.common.closeMenu}
              data-testid="mobile-menu-close"
            >
              <X size={20} />
            </button>
          </div>
          <nav className="container-x mt-8 flex flex-col gap-2">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                data-testid={`mobile-${n.testid}`}
                className={({ isActive }) =>
                  cn(
                    "py-4 text-2xl font-display font-semibold border-b border-border",
                    isActive ? "text-primary" : "text-foreground"
                  )
                }
              >
                {n.label}
              </NavLink>
            ))}
            <a
              href={COMPANY.phoneHref}
              onClick={() => setOpen(false)}
              data-testid="mobile-menu-call-now"
              className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-primary text-primary-foreground py-4 text-lg font-semibold"
            >
              <Phone size={18} /> {t.common.call} {COMPANY.phoneDisplay}
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
